/**
 * Pooled raycast projectiles (spec §6, D-013, D-014): a fixed-capacity structure-of-arrays pool, not ECS
 * entities. Each tick a projectile moves one step and raycasts from its previous position to its new one
 * (excluding its owner), so nothing passes through a thin target at any speed. Allocation-free.
 */
import type { WeaponsData } from '../data/schemas'
import { createRay, setRay, type RayHit, type RaycastFn } from '../physics/physics'
import { Life, MAX_ENTITIES, Pos64, Team } from '../world/components'
import { applyDamage, createDamageResult } from './damage'
import { emitEvent, EV, type EventRing } from './events'
import { notePlayerDamage } from './hostility'

/** Default capacity; the game passes `weapons.json` `pools.projectiles`. */
export const DEFAULT_PROJECTILE_CAPACITY = 2048

export interface ProjectilePool {
  readonly capacity: number
  /** Float64 world position after this tick's step (D-011). */
  x: Float64Array
  y: Float64Array
  z: Float64Array
  /** Float64 world position before this tick's step (render interpolation and streak tails). */
  px: Float64Array
  py: Float64Array
  pz: Float64Array
  /** World velocity, m/s. */
  vx: Float32Array
  vy: Float32Array
  vz: Float32Array
  /** Seconds of flight left (range ÷ speed at spawn). */
  ttl: Float32Array
  /** Shooter entity id (excluded from its own rays). */
  owner: Int32Array
  team: Uint8Array
  /** Index into `WeaponsData.weapons`. */
  weapon: Uint8Array
  alive: Uint8Array
  /** The owner's shot number (0, 1, 2, ...): a round draws a tracer when `seq % tracerEvery === 0` (see `isTracer`). */
  seq: Uint32Array
  /** Free-index stack; `free[freeCount - 1]` is the next index handed out (lowest first). */
  free: Int32Array
  freeCount: number
  /** Iterate live projectiles over `0 <= i < high` and check `alive[i]`. */
  high: number
  /** Live projectile count. */
  live: number
  /** Shots dropped because the pool was full (perf overlay). */
  drops: number
  /** Per-owner shot counters for `seq`, indexed by entity id. */
  shots: Uint32Array
}

export function createProjectilePool(capacity: number = DEFAULT_PROJECTILE_CAPACITY, maxOwners: number = MAX_ENTITIES): ProjectilePool {
  if (!Number.isInteger(capacity) || capacity < 1) throw new Error(`Projectile pool capacity must be a positive integer (got ${capacity})`)
  const free = new Int32Array(capacity)
  for (let i = 0; i < capacity; i++) free[i] = capacity - 1 - i
  return {
    capacity,
    x: new Float64Array(capacity),
    y: new Float64Array(capacity),
    z: new Float64Array(capacity),
    px: new Float64Array(capacity),
    py: new Float64Array(capacity),
    pz: new Float64Array(capacity),
    vx: new Float32Array(capacity),
    vy: new Float32Array(capacity),
    vz: new Float32Array(capacity),
    ttl: new Float32Array(capacity),
    owner: new Int32Array(capacity),
    team: new Uint8Array(capacity),
    weapon: new Uint8Array(capacity),
    alive: new Uint8Array(capacity),
    seq: new Uint32Array(capacity),
    free,
    freeCount: capacity,
    high: 0,
    live: 0,
    drops: 0,
    shots: new Uint32Array(maxOwners),
  }
}

/**
 * Spawns a projectile at float64 world (x, y, z) with world velocity (vx, vy, vz) and `ttl` seconds of
 * flight. Returns its index, or -1 when the pool is full (the shot is dropped and counted in `drops`).
 */
export function spawnProjectile(
  pool: ProjectilePool,
  x: number,
  y: number,
  z: number,
  vx: number,
  vy: number,
  vz: number,
  ttl: number,
  owner: number,
  team: number,
  weapon: number,
): number {
  if (pool.freeCount === 0) {
    pool.drops++
    return -1
  }
  const i = pool.free[--pool.freeCount]
  pool.x[i] = pool.px[i] = x
  pool.y[i] = pool.py[i] = y
  pool.z[i] = pool.pz[i] = z
  pool.vx[i] = vx
  pool.vy[i] = vy
  pool.vz[i] = vz
  pool.ttl[i] = ttl
  pool.owner[i] = owner
  pool.team[i] = team
  pool.weapon[i] = weapon
  pool.alive[i] = 1
  if (owner >= 0 && owner < pool.shots.length) pool.seq[i] = pool.shots[owner]++
  else pool.seq[i] = 0
  if (i >= pool.high) pool.high = i + 1
  pool.live++
  return i
}

/** Returns projectile `i` to the pool. No-op when it is not alive. */
export function freeProjectile(pool: ProjectilePool, i: number): void {
  if (pool.alive[i] !== 1) return
  pool.alive[i] = 0
  pool.free[pool.freeCount++] = i
  pool.live--
  // Shrink the iteration bound past trailing dead slots.
  while (pool.high > 0 && pool.alive[pool.high - 1] === 0) pool.high--
}

/** Frees every projectile and resets the counters (new game). */
export function clearProjectiles(pool: ProjectilePool): void {
  for (let i = 0; i < pool.high; i++) pool.alive[i] = 0
  for (let i = 0; i < pool.capacity; i++) pool.free[i] = pool.capacity - 1 - i
  pool.freeCount = pool.capacity
  pool.high = 0
  pool.live = 0
  pool.drops = 0
  pool.shots.fill(0)
}

/** True when projectile `i` should draw a tracer (every `tracerEvery`-th round of its owner, starting with the first). */
export function isTracer(pool: ProjectilePool, i: number, tracerEvery: number): boolean {
  return tracerEvery <= 1 || pool.seq[i] % tracerEvery === 0
}

const damage = createDamageResult()
/** Float32 `ttl` rounding slack (s): a round this close to its end expires now instead of one sub-millimetre tick later. */
const TTL_EPS = 1e-5

/**
 * Handles a hit of projectile `i` at `hit`. Ships of another team take the weapon's damage and produce
 * `hitShield` (amount = shield absorbed), `hitHull` (amount = hull damage), `shieldDown` and `kill`
 * events. A hit that deals no damage (a rock or the derelict, a same-team ship: friendly fire is off in
 * M1, or an already dead ship) emits one `hitHull` with amount 0 so effects still show the impact;
 * readers that only care about damage check `amount > 0` and `eid >= 0`.
 */
function resolveHit(pool: ProjectilePool, i: number, weapons: WeaponsData, hit: RayHit, events: EventRing, now: number): void {
  const w = pool.weapon[i]
  const def = weapons.weapons[w]
  const victim = hit.eid
  const attacker = pool.owner[i]
  let dealt = false
  if (victim >= 0 && Team.id[victim] !== pool.team[i] && Life.alive[victim] === 1) {
    applyDamage(victim, def.damage, def.vsShield, def.vsHull, now, damage)
    if (damage.shieldAbsorbed > 0) {
      emitEvent(events, EV.hitShield, hit.x, hit.y, hit.z, hit.nx, hit.ny, hit.nz, victim, attacker, w, damage.shieldAbsorbed)
      dealt = true
    }
    if (damage.shieldDown) {
      emitEvent(events, EV.shieldDown, Pos64.x[victim], Pos64.y[victim], Pos64.z[victim], 0, 0, 0, victim, attacker, w, 0)
    }
    if (damage.hullDamage > 0) {
      emitEvent(events, EV.hitHull, hit.x, hit.y, hit.z, hit.nx, hit.ny, hit.nz, victim, attacker, w, damage.hullDamage)
      dealt = true
    }
    if (damage.killed) {
      emitEvent(events, EV.kill, Pos64.x[victim], Pos64.y[victim], Pos64.z[victim], 0, 0, 0, victim, attacker, w, 0)
    }
  }
  if (dealt) notePlayerDamage(attacker, victim, now)
  if (!dealt) emitEvent(events, EV.hitHull, hit.x, hit.y, hit.z, hit.nx, hit.ny, hit.nz, victim, attacker, w, 0)
}

/**
 * Advances every live projectile by `dt` (the last step is clipped to the remaining `ttl`, so a round
 * travels exactly its range), raycasts the step excluding the owner, resolves a hit at the closest
 * surface and frees the projectile, or frees it when its `ttl` runs out. `hitOut` is caller-owned scratch.
 */
/** Ray scratch (D-014). */
const ray = createRay()

export function stepProjectiles(
  pool: ProjectilePool,
  weapons: WeaponsData,
  raycast: RaycastFn,
  events: EventRing,
  now: number,
  dt: number,
  hitOut: RayHit,
): void {
  const high = pool.high
  for (let i = 0; i < high; i++) {
    if (pool.alive[i] !== 1) continue
    const ttl = pool.ttl[i]
    const step = ttl < dt ? ttl : dt
    const ox = pool.x[i]
    const oy = pool.y[i]
    const oz = pool.z[i]
    const mx = pool.vx[i] * step
    const my = pool.vy[i] * step
    const mz = pool.vz[i] * step
    pool.px[i] = ox
    pool.py[i] = oy
    pool.pz[i] = oz
    pool.x[i] = ox + mx
    pool.y[i] = oy + my
    pool.z[i] = oz + mz
    pool.ttl[i] = ttl - step
    const len = Math.sqrt(mx * mx + my * my + mz * mz)
    if (len > 0 && raycast(setRay(ray, ox, oy, oz, mx / len, my / len, mz / len, len), pool.owner[i], hitOut)) {
      pool.x[i] = hitOut.x
      pool.y[i] = hitOut.y
      pool.z[i] = hitOut.z
      resolveHit(pool, i, weapons, hitOut, events, now)
      freeProjectile(pool, i)
      continue
    }
    if (ttl - step <= TTL_EPS) freeProjectile(pool, i)
  }
}
